/**
 * Subscription plan catalog — the single display source of truth for tiers,
 * consumed by BOTH the Settings subscription tab and the public pricing page so
 * the two can never drift.
 *
 * This is the *presentation* layer. The authoritative entitlements (full
 * feature flags, limits, Stripe price IDs used for billing) live backend-side;
 * the prices and `stripePriceId`s here mirror that config for display and for
 * mapping a chosen tier to its Stripe price.
 */

import type { PlanName, SubscriptionTier } from '../types/auth';

export interface PlanCatalogEntry {
  tier: SubscriptionTier;
  /** Display name — 'Free' | 'Pro' | 'Premium'. */
  name: string;
  /** Monthly price in major units (USD). 0 for Free. */
  price: number;
  currency: string;
  /** Stripe recurring price id, or null for Free (no Stripe price). */
  stripePriceId: string | null;
  description: string;
  /**
   * Curated benefit highlights for the plan cards. Tiers are cumulative, so
   * Pro/Premium read as "Everything in {lower}, plus" these.
   */
  highlights: readonly string[];
  /** One-line headline of the tier's key limits. */
  limitsSummary: string;
}

/** Display order, cheapest first. */
export const PLAN_ORDER: readonly SubscriptionTier[] = [
  'free',
  'pro',
  'premium',
];

export const SUBSCRIPTION_PLANS: Record<SubscriptionTier, PlanCatalogEntry> = {
  free: {
    tier: 'free',
    name: 'Free',
    price: 0,
    currency: 'CAD',
    stripePriceId: null,
    description: 'Essential workout tracking and basic progress monitoring.',
    highlights: [
      'Unlimited workouts & PRs',
      'Exercise library & timers',
      'Basic measurements & progress',
      'Activity feed & global leaderboards',
    ],
    limitsSummary: '1 plan · 5 custom exercises · 30-day history',
  },
  pro: {
    tier: 'pro',
    name: 'Pro',
    price: 9.99,
    currency: 'CAD',
    stripePriceId: 'price_1S1xxABVSadSErRNM3fp5PBr',
    description:
      'Advanced analytics, social features, and comprehensive tracking.',
    highlights: [
      'Full analytics & trend insights',
      'Progress photos & body composition',
      'Workout sharing & every challenge type',
      'Monthly reports & PDF export',
    ],
    limitsSummary: '50 exercises · 3 plans · 6-month history',
  },
  premium: {
    tier: 'premium',
    name: 'Premium',
    price: 19.99,
    currency: 'CAD',
    stripePriceId: 'price_1S1xxYBVSadSErRNUzazQI6H',
    description:
      'Professional features, unlimited access, and advanced reporting.',
    highlights: [
      'Trainer tools & client management',
      'Unlimited everything — no caps',
      'Performance predictions & health insights',
      'Unlimited progress reports',
      'Priority support',
    ],
    limitsSummary: 'No limits',
  },
};

/** Lookup a tier's catalog entry. */
export const getPlan = (tier: SubscriptionTier): PlanCatalogEntry =>
  SUBSCRIPTION_PLANS[tier];

/** Map an API display name ("Pro") back to its tier ("pro"). */
export const tierFromPlanName = (name: string): SubscriptionTier => {
  const match = PLAN_ORDER.find(
    (tier) => SUBSCRIPTION_PLANS[tier].name === name
  );
  return match ?? 'free';
};

/** Format a tier's price for display, e.g. 'CA$0' or 'CA$9.99' (matches the API). */
export const formatPlanPrice = (entry: PlanCatalogEntry): string =>
  `CA$${entry.price.toFixed(2)}`;

/** Numeric rank for comparing tiers (free < pro < premium). */
export const planRank = (tier: SubscriptionTier): number =>
  PLAN_ORDER.indexOf(tier);

/**
 * Map a backend `PlanName` ("Pro Plan") to its tier ("pro"). Distinct from
 * {@link tierFromPlanName}, which maps a display *name* ("Pro") — the auth
 * user's `subscription.plan` is a `PlanName`, so feeding it to `tierFromPlanName`
 * silently yields "free".
 */
export const planNameToTier = (name: PlanName): SubscriptionTier => {
  switch (name) {
    case 'Pro Plan':
      return 'pro';
    case 'Premium Plan':
      return 'premium';
    default:
      return 'free';
  }
};

/**
 * Curated feature comparison for the public pricing page. Deliberately a
 * headline subset (not the full entitlements matrix) — the values are derived
 * from the backend entitlements config.
 */
export interface PlanComparisonRow {
  label: string;
  values: Record<SubscriptionTier, boolean | string>;
}

export const PLAN_COMPARISON: readonly PlanComparisonRow[] = [
  {
    label: 'Workout & PR tracking',
    values: { free: true, pro: true, premium: true },
  },
  {
    label: 'Custom exercises',
    values: { free: '5', pro: '50', premium: 'Unlimited' },
  },
  {
    label: 'Active plans',
    values: { free: '1', pro: '3', premium: 'Unlimited' },
  },
  {
    label: 'Data history',
    values: { free: '30 days', pro: '6 months', premium: 'Unlimited' },
  },
  {
    label: 'Progress photos',
    values: { free: false, pro: true, premium: true },
  },
  {
    label: 'Advanced analytics & insights',
    values: { free: false, pro: true, premium: true },
  },
  {
    label: 'Friends',
    values: { free: '5', pro: '50', premium: 'Unlimited' },
  },
  {
    label: 'Active challenges',
    values: { free: '1', pro: '3', premium: 'Unlimited' },
  },
  {
    // `activity_feed`, `global_leaderboards` and `achievement_badges` are Free
    // features since the 2026-10-22 entitlements change; they were paid.
    label: 'Activity feed & leaderboards',
    values: { free: true, pro: true, premium: true },
  },
  {
    // What Free still loses from the social category: `workout_sharing` and
    // `challenge_types_all` stay in its `restricted_features`.
    label: 'Workout sharing & all challenge types',
    values: { free: false, pro: true, premium: true },
  },
  {
    label: 'Progress reports',
    values: { free: false, pro: 'Monthly', premium: 'Unlimited' },
  },
  {
    // The workout history export: CSV on every plan, Pro and Premium add PDF
    // (`data_export_pdf`). Not the full account export in Settings, which is a
    // free JSON file on every plan.
    label: 'Workout history export',
    values: { free: 'CSV', pro: 'CSV + PDF', premium: 'CSV + PDF' },
  },
  {
    label: 'Performance predictions',
    values: { free: false, pro: false, premium: true },
  },
  {
    label: 'Trainer & client tools',
    values: { free: false, pro: false, premium: true },
  },
  {
    label: 'Priority support',
    values: { free: false, pro: false, premium: true },
  },
];
